import { styled } from '@mui/material';
import { RouteFn, SubmitFn } from '@/hooks/use-flow';
import { DynamicRenderer } from './DynamicUI/DynamicRenderer';
import { Component, SceneData } from '@/schemas/sceneDataSchema';
import { Error as ErrorComponent } from './Error';
import { Footer } from './Footer';
import { ComponentsTypes } from '@/constants/componentsTypes';
import { CoverageSceneWrapper } from './DynamicUI/CoverageSceneWrapper';

function extractSlots(components: Component[]) {
  const slots: Record<string, Component[]> = {};
  const rest: Component[] = [];

  function recurse(items: Component[]) {
    for (const c of items) {
      if (c.type === ComponentsTypes.SLOT) {
        const slotComponent = c as unknown as { id: string; components: Component[] };
        slots[slotComponent.id] = slotComponent.components;
      } else {
        rest.push(c);
        if ('components' in c && Array.isArray(c.components)) {
          recurse(c.components);
        }
      }
    }
  }

  recurse(components);
  return { slots, rest };
}

function assertRequiredSlotsPresent(slots: Record<string, Component[]>, requiredSlots: string[], sceneVariant: string) {
  const missingSlots = requiredSlots.filter((slot) => !slots[slot]);
  if (missingSlots.length > 0) {
    throw new Error(
      `Variant "${sceneVariant}" requires slots [${requiredSlots.join(', ')}] but received [${Object.keys(slots).join(', ')}]. Missing: [${missingSlots.join(', ')}]`
    );
  }
}

interface SceneWrapProps {
  sceneData?: SceneData;
  route: RouteFn;
  submit: SubmitFn;
}

export function SceneWrap({ sceneData, route, submit }: SceneWrapProps) {
  if (!sceneData) {
    return <ErrorComponent />;
  }

  const variant = sceneData.scene.variant || 'center_column_gray';

  if (variant === 'vertically_centered') {
    return (
      <VerticallyCenteredLayout>
        <DynamicRenderer
          components={sceneData.scene.components}
          route={route}
          submit={submit}
        />
      </VerticallyCenteredLayout>
    );
  }

  if (variant === 'center_column_gray' || variant === 'center_column_white') {
    return (
      <CenterColumnLayout
        backgroundColor={variant === 'center_column_white' ? 'main' : 'secondary'}
        slotMain={
          <DynamicRenderer
            components={sceneData.scene.components}
            route={route}
            submit={submit}
          />
        }
        slotFooter={
          sceneData.scene.footer ? (
            <DynamicRenderer
              components={sceneData.scene.footer}
              route={route}
              submit={submit}
            />
          ) : null
        }
      ></CenterColumnLayout>
    );
  }

  if (variant === 'elements_gallery') {
    return (
      <ElementsGalleryLayout>
        <DynamicRenderer
          components={sceneData.scene.components}
          route={route}
          submit={submit}
        />
      </ElementsGalleryLayout>
    );
  }

  if (variant === 'quote_customization') {
    const wrapperComponent = sceneData.scene.components.find((c) => c.type === ComponentsTypes.COVERAGE_SCENE_WRAPPER);

    if (!wrapperComponent) {
      throw new Error('The quote_customization scene variant must have a CoverageSceneWrapper component.');
    }

    const { slots } = extractSlots(wrapperComponent.components);
    assertRequiredSlotsPresent(slots, ['aside', 'main', 'color_band'], variant);

    return (
      <CoverageSceneWrapper initial_state={wrapperComponent.initial_state}>
        <QuoteCustomizationLayout>
          <div className='content-wrap'>
            <aside className='slot-aside'>
              <DynamicRenderer
                components={slots.aside}
                route={route}
                submit={submit}
              />
            </aside>
            <main className='slot-main'>
              <div className='slot-color-band'>
                <DynamicRenderer
                  components={slots.color_band}
                  route={route}
                  submit={submit}
                />
              </div>
              <DynamicRenderer
                components={slots.main}
                route={route}
                submit={submit}
              />
            </main>
          </div>
        </QuoteCustomizationLayout>
      </CoverageSceneWrapper>
    );
  }

  if (variant === 'agent_layout') {
    return (
      <AgentLayout>
        <div className='content-wrap'>
          <DynamicRenderer
            components={sceneData.scene.components}
            route={route}
            submit={submit}
          />
        </div>
      </AgentLayout>
    );
  }

  if (variant === 'card_columns') {
    const cards = sceneData.scene.components.filter((c) => c.type === ComponentsTypes.CARD);
    const nonCards = sceneData.scene.components.filter((c) => c.type !== ComponentsTypes.CARD);

    return (
      <CardColumnsLayout>
        <div className='content-wrap'>
          <DynamicRenderer
            components={nonCards}
            route={route}
            submit={submit}
          />
          <div className={'cards-container'}>
            <DynamicRenderer
              components={cards}
              route={route}
              submit={submit}
            />
          </div>
        </div>
      </CardColumnsLayout>
    );
  }

  throw new Error(`Unknown scene variant: ${variant}`);
}

// This layout was designed specifically for centering our caret loader scenes
// but could be extended in the future if needed for more use cases.
const VerticallyCenteredLayout = styled('main')`
  min-height: calc(100svh - var(--app-header-height));
  margin-top: var(--app-header-height);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 20px 80px;
  background-color: var(--mui-palette-background-main);

  & > * {
    width: min(var(--scene-max-width), 100%);
  }
`;

interface CenterColumnLayoutProps {
  slotMain: React.ReactNode;
  slotFooter?: React.ReactNode;
  backgroundColor?: 'main' | 'secondary';
  columnWidth?: 'default' | 'medium' | 'wide';
}

export function CenterColumnLayout({
  slotMain,
  slotFooter,
  backgroundColor = 'secondary',
  columnWidth = 'default',
}: CenterColumnLayoutProps) {
  return (
    <CenterColumn
      className='center-column'
      backgroundColor={backgroundColor}
      columnWidth={columnWidth}
    >
      <main className='content-wrap'>{slotMain}</main>
      {slotFooter && <Footer>{slotFooter}</Footer>}
    </CenterColumn>
  );
}

interface CenterColumnProps {
  backgroundColor?: 'main' | 'secondary';
  columnWidth?: 'default' | 'medium' | 'wide';
}

const CenterColumn = styled('div', {
  shouldForwardProp: (prop) => prop !== 'backgroundColor' && prop !== 'columnWidth',
})<CenterColumnProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  min-height: calc(100svh - var(--app-header-height));
  margin-top: var(--app-header-height);
  padding-top: calc(min(100px, 8vw));
  padding-bottom: 40px;
  padding-inline: 14px;
  background-color: ${({ backgroundColor }) =>
    backgroundColor === 'main' ? 'var(--mui-palette-background-main)' : 'var(--mui-palette-background-secondary)'};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 380px;
    z-index: 0;
    background-color: var(--mui-palette-background-main);
  }

  & > .content-wrap {
    width: ${({ columnWidth }) => {
      switch (columnWidth) {
        case 'wide':
          return '90vw';
        case 'medium':
          return '840px';
        default:
          return 'min(var(--scene-max-width), 100%)';
      }
    }};
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  & > footer {
    margin-top: 40px;
  }

  @media (max-width: 480px) {
    background-color: var(--mui-palette-background-main);

    &::before {
      display: none;
    }

    & .section-container {
      border: none;
      border-radius: 0;

      > .MuiCardContent-root {
        padding: 0;
      }
    }
  }
`;

const ElementsGalleryLayout = styled('div')`
  display: flex;
  background-color: var(--mui-palette-background-secondary);

  /* Sidebar (first section container) */
  & > .section-container:first-of-type {
    border: none;
    border-radius: 0;
    width: 280px;
    position: sticky;
    top: 0;
    border-right: 1px solid var(--mui-palette-divider);
    padding-top: calc(var(--app-header-height));
    max-height: 100vh;
    overflow-y: auto;
  }
  & > .section-container:first-of-type .MuiCardContent-root {
    padding: 16px 16px;
  }

  /* Main content (second section container) */
  & > .section-container:last-of-type {
    flex: 1;
    padding-top: calc(var(--app-header-height) + 20px);
    padding-inline: 20px;
    column-count: auto;
    column-width: 400px;
    column-gap: 20px;
    background-color: transparent;
    border: none;
  }

  /* Give space between cards */
  & > .section-container:last-of-type > * {
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    flex-direction: column;

    /* Hide the table of contents on mobile */
    & > .section-container:first-of-type a {
      display: none;
    }

    & > .section-container:first-of-type {
      position: relative;
      width: 100%;
      border-bottom: 1px solid var(--mui-palette-divider);
    }

    /* Main content */
    & > .section-container:last-of-type {
      padding-inline: 12px;
      padding-top: 20px;
    }

    & > .section-container:last-of-type > * {
      margin: 0;
    }

    & > .section-container:last-of-type > * + * {
      margin-top: 16px;
    }
  }
`;

const QuoteCustomizationLayout = styled('div')`
  --color-band-height: 400px;

  display: flex;
  justify-content: center;
  min-height: calc(100svh - var(--app-header-height));
  margin-top: var(--app-header-height);
  padding-bottom: 40px;
  padding-inline: 20px;
  background-color: var(--mui-palette-background-secondary);

  &::before {
    content: '';
    position: absolute;
    top: var(--app-header-height);
    left: 0;
    width: 100%;
    height: var(--color-band-height);
    background-color: var(--mui-palette-background-main);
  }

  & > .content-wrap {
    width: min(1200px, 100%);
    position: relative;
    display: flex;
    flex-direction: row-reverse;
    gap: 30px;
  }

  .slot-aside {
    position: sticky;
    top: calc(var(--app-header-height) + 20px);
    align-self: flex-start;
    width: 360px;
    flex-shrink: 0;
  }

  .slot-color-band {
    height: var(--color-band-height);
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .slot-main {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  @media (max-width: 768px) {
    --mobile-spacing-between-sections: 14px;
    padding: 0px;

    &::before,
    .slot-color-band {
      display: none;
    }

    & > .content-wrap {
      flex-direction: column;
      gap: var(--mobile-spacing-between-sections);
    }

    .slot-aside {
      position: relative;
      top: 0;
      width: 100%;

      & > .MuiCard-root {
        border-radius: 0;
        border-top: none;
        border-left: none;
        border-right: none;
        padding-bottom: 10px;
      }
    }

    .slot-main {
      gap: var(--mobile-spacing-between-sections);

      & > .MuiCard-root {
        border-radius: 0;
        border-left: none;
        border-right: none;
        padding-bottom: 10px;
      }
    }
  }
`;

const CardColumnsLayout = styled('div')`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: calc(100svh - var(--app-header-height));
  margin-top: var(--app-header-height);
  padding-top: calc(min(100px, 8vw));
  padding-bottom: 40px;
  padding-inline: 14px;
  background-color: var(--mui-palette-background-secondary);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 380px;
    z-index: 0;
    background-color: var(--mui-palette-background-main);
  }

  & > .content-wrap {
    width: min(1200px, 100%);
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 70px;
  }

  & .cards-container {
    display: flex;
    gap: 20px;
    width: 100%;

    > .MuiCard-root {
      flex: 1;

      /* 
        It would be great to have the ability to do this from the server config but unfortunately 
        making it happen is complex and involves orchestrating several layers of the card. 
        Handling it here, specifically for this scene for now. 
        👇
      */

      /* START: Push CTA to bottom of card */
      > .MuiCardContent-root {
        height: 100%;

        > .MuiStack-root {
          height: 100%;
        }
      }

      .MuiButton-root {
        margin-top: auto;
      }
      /* END: Push CTA to bottom of card */
    }
  }

  @media (max-width: 1050px) {
    & .cards-container {
      flex-direction: column;
      width: min(100%, 520px);
    }
  }
`;

const AgentLayout = styled('div')`
  display: flex;
  justify-content: center;
  min-height: calc(100svh - var(--app-header-height));
  margin-top: 82px;
  background-color: var(--mui-palette-background-secondary);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 330px;
    z-index: 0;
    background-color: var(--mui-palette-background-main);
  }

  & > .content-wrap {
    width: min(1144px, 100%); /* 840px + 264px + 40px gap */
    position: relative;
    padding-bottom: 40px;
    padding-inline: 20px;
    z-index: 1;
  }
`;
